// src/views/catalog/CatalogEntriesView.tsx
import React, {
  FC, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import {
  List, Popconfirm, Spin, Tooltip, message,
} from 'antd';
import {
  CloseCircleFilled, CloseOutlined, EditOutlined, LoadingOutlined, SearchOutlined,
} from '@ant-design/icons';
import {
  TbPhoto, TbPhotoPlus, TbSquareRoundedPlus, TbStack2, TbUpload,
} from 'react-icons/tb';
import API, { CatalogEntry } from '../../services/ApiService';
import { getCanCurate } from '../auth/slice/selectors';
import { fetchMe } from '../auth/slice/actions';
import { error, success } from '../../components/Message';
import { ScreenshotUpload } from '../../components/ScreenshotUpload/ScreenshotUpload';
import { ACCEPTED_PHOTO_TYPES, buildPhotoFormData, validatePhotoFile } from '../../utils/photoUpload';
import {
  MAX_CATALOG_TERM_LENGTH, catalogErrorCode, catalogErrorKey, isCatalogTerm,
} from '../../utils/catalog';
import { matchesSearch, toSearchWords } from '../../utils/search';
import withTagManager from '../../hooks/withTagManager';
import { useCatalogTermEdit } from './useCatalogTermEdit';
import classes from './CatalogEntriesView.module.less';

// Obraz czeka w przeglądarce — na serwer idzie dopiero przy „Dodaj do katalogu”. `assetId` pojawia się
// po udanym wysłaniu, więc ponowna próba po nieudanym dodaniu nie wysyła pliku drugi raz.
type PendingImage = { file: File; previewUrl: string; assetId?: number };

type ConceptGroup = { conceptKey: string; entries: CatalogEntry[] };

// Jeden wiersz na zwrot, zwroty alfabetycznie. Nie zakładamy kolejności z backendu — kontrakt jej
// nie gwarantuje. Wewnątrz zwrotu obrazki zostają w kolejności odpowiedzi, czyli dodania.
const groupByConcept = (entries: CatalogEntry[]): ConceptGroup[] => {
  const groups = new Map<string, CatalogEntry[]>();

  entries.forEach((entry) => {
    groups.set(entry.conceptKey, [...(groups.get(entry.conceptKey) || []), entry]);
  });

  return Array.from(groups, ([conceptKey, conceptEntries]) => ({ conceptKey, entries: conceptEntries }))
    .sort((a, b) => a.conceptKey.localeCompare(b.conceptKey));
};

// Katalog kuratora: dodawanie par zwrot → obrazek wprost, bez listy, poprawianie zwrotów i usuwanie
// par z katalogu. Usunięcie jest miękkie i odwracalne — ponowne dodanie tej samej pary ją przywraca.
const CatalogEntriesView: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const canCurate = useSelector(getCanCurate);
  const [entries, setEntries] = useState<CatalogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [withdrawingId, setWithdrawingId] = useState<number | null>(null);

  const [term, setTerm] = useState('');
  const [image, setImage] = useState<PendingImage | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadEntries = useCallback(async () => {
    setIsLoading(true);

    try {
      const { data } = await API.catalog.entries();

      setEntries(data.entries);
    } catch (e) {
      error(t(catalogErrorKey(e)));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (canCurate) {
      loadEntries();
    }
  }, [canCurate, loadEntries]);

  // Podgląd to adres pliku w pamięci przeglądarki — zwalniamy go przy podmianie obrazu i wyjściu z ekranu
  const previewUrl = image?.previewUrl;

  useEffect(() => () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
  }, [previewUrl]);

  // Wklejony, przeciągnięty albo wybrany z dysku obraz to dopiero kandydat: zostaje w przeglądarce,
  // a porzucony nie zostawia na serwerze pliku, którego nikt nie używa
  const acceptFile = useCallback((file: File) => {
    setImage({ file, previewUrl: URL.createObjectURL(file) });
  }, []);

  // Plik z okna wyboru przechodzi te same reguły co wklejony
  const pickFromDisk = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    // Czyścimy od razu — inaczej ponowny wybór tego samego pliku nie wywoła onChange
    e.target.value = '';

    if (!file) {
      return;
    }

    const validationError = validatePhotoFile(file);
    if (validationError) {
      message.error(validationError);

      return;
    }

    acceptFile(file);
  }, [acceptFile]);

  // Upload bez `wordsListId` — obraz spoza listy, backend odkłada go osobno. `null` znaczy, że się
  // nie udało, a komunikat już poszedł.
  const uploadImage = useCallback(async (pending: PendingImage): Promise<number | null> => {
    if (pending.assetId) {
      return pending.assetId;
    }

    try {
      const { data } = await API.photos.add(buildPhotoFormData(pending.file));

      if (!data.assetId) {
        error(t('words-list:catalog.entries.uploadError'));

        return null;
      }

      // Tylko jeśli w podglądzie wciąż jest ten obraz — w trakcie wysyłania mógł przyjść następny
      setImage((current) => (
        current?.previewUrl === pending.previewUrl ? { ...current, assetId: data.assetId } : current
      ));

      return data.assetId;
    } catch {
      error(t('words-list:photo.messages.error'));

      return null;
    }
  }, [t]);

  const addEntry = useCallback(async () => {
    if (!image || !isCatalogTerm(term) || isAdding) {
      return;
    }

    setIsAdding(true);

    try {
      const assetId = await uploadImage(image);

      if (!assetId) {
        return;
      }

      const { status } = await API.catalog.add(term, assetId);

      success(t(status === 200 ? 'words-list:catalog.messages.restored' : 'words-list:catalog.messages.added'));
      setTerm('');
      // W trakcie dodawania kurator mógł wkleić już następny obraz — ten zostaje w podglądzie
      setImage((current) => (current?.previewUrl === image.previewUrl ? null : current));
      loadEntries();
    } catch (e) {
      const code = catalogErrorCode(e);

      if (code === 'CATALOG_ENTRY_EXISTS') {
        message.info(t(catalogErrorKey(e)));

        return;
      }

      error(t(catalogErrorKey(e)));

      // Uprawnienia zdjęto w trakcie sesji — odświeżony profil schowa ten ekran
      if (code === 'CATALOG_NOT_CURATOR') {
        dispatch(fetchMe());
      }
    } finally {
      setIsAdding(false);
    }
  }, [dispatch, image, isAdding, loadEntries, t, term, uploadImage]);

  const withdraw = useCallback(async (entry: CatalogEntry) => {
    setWithdrawingId(entry.id);

    try {
      await API.catalog.withdraw(entry.id);

      setEntries((current) => current.map((item) => (
        item.id === entry.id ? { ...item, active: false } : item
      )));
      success(t('words-list:catalog.entries.withdrawSuccess'));
    } catch (e) {
      // 404 znaczy, że wpisu już nie ma — widok trzeba odświeżyć
      error(t(catalogErrorKey(e)));
      loadEntries();
    } finally {
      setWithdrawingId(null);
    }
  }, [loadEntries, t]);

  const {
    termEdit, isSavingTerm, termEditRef, highlightedKey, highlightedTermRef,
    startTermEdit, changeTermDraft, saveTermEdit,
  } = useCatalogTermEdit({ entries, loadEntries, setFilter });

  // Ta sama reguła co na ekranie z listami: słowa w dowolnej kolejności, a znaki inne niż litery i cyfry
  // nie przeszkadzają. Puste pole albo same znaki (np. „...") nie filtrują niczego.
  const searchWords = useMemo(() => toSearchWords(filter), [filter]);

  // Usunięte wpisy nie są pokazywane
  const groups = useMemo(() => groupByConcept(entries.filter((entry) => (
    entry.active && matchesSearch(entry.conceptKey, searchWords)
  ))), [entries, searchWords]);

  // Licznik pozycji liczy wiersze listy, czyli zwroty — nie obrazki. Usunięte wpisy się nie liczą.
  const totalCount = useMemo(() => (
    new Set(entries.filter((entry) => entry.active).map((entry) => entry.conceptKey)).size
  ), [entries]);

  // Zwrot wiersza z ołówkiem w rogu, a po kliknięciu ołówka w tym samym miejscu pole poprawki.
  // Enter, Esc albo kliknięcie obok zapisują. Przejście do innej karty czy okna nie — po powrocie
  // poprawia się dalej.
  const renderTerm = (group: ConceptGroup) => {
    const draft = termEdit?.conceptKey === group.conceptKey ? termEdit.draft : null;

    return (
      <span
        ref={group.conceptKey === highlightedKey ? highlightedTermRef : undefined}
        className={classes.term}
      >
        {draft !== null ? (
          <input
            ref={termEditRef}
            className={classes.termEditField}
            aria-label={t('words-list:catalog.entries.editTerm')}
            maxLength={MAX_CATALOG_TERM_LENGTH}
            value={draft}
            readOnly={isSavingTerm}
            onChange={(e) => changeTermDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === 'Escape') {
                saveTermEdit(group.conceptKey, draft);
              }
            }}
            onBlur={() => {
              if (document.hasFocus()) {
                saveTermEdit(group.conceptKey, draft);
              }
            }}
          />
        ) : group.conceptKey}

        {/* W trakcie zapisu w miejscu ołówka kręci się kółko, a ołówki innych wierszy czekają */}
        {(draft === null || isSavingTerm) && (
          <button
            type="button"
            className={classes.editButton}
            disabled={isSavingTerm}
            aria-label={t('words-list:catalog.entries.editTerm')}
            title={t('words-list:catalog.entries.editTerm')}
            onClick={() => startTermEdit(group.conceptKey)}
          >
            {draft === null ? <EditOutlined aria-hidden="true" /> : <LoadingOutlined aria-hidden="true" />}
          </button>
        )}
      </span>
    );
  };

  if (!canCurate) {
    return (
      <div className={classes.wrapper}>
        <p className={classes.notice}>{t('words-list:catalog.entries.notCurator')}</p>
      </div>
    );
  }

  return (
    <div className={classes.wrapper}>
      {/* Wklejenie (Ctrl+V) i przeciągnięcie obrazu działają na całym ekranie; plik czeka na „Dodaj” */}
      <ScreenshotUpload onFileAccepted={acceptFile} isActive />

      {/* Nowa para: po lewej miejsce na obraz, po prawej zwrot nad przyciskami — razem na całą szerokość */}
      <div className={classes.addForm}>
        <div className={classes.imageSlot}>
          {image ? (
            <img className={classes.preview} src={image.previewUrl} alt={term} />
          ) : (
            <>
              <span className={classes.imageHint}>{t('words-list:catalog.entries.imageHint')}</span>
              <TbPhotoPlus className={classes.imageHintIcon} />
            </>
          )}
        </div>

        <div className={classes.fields}>
          <input
            className={classes.termField}
            placeholder={t('words-list:catalog.entries.termPlaceholder')}
            aria-label={t('words-list:catalog.entries.termPlaceholder')}
            maxLength={MAX_CATALOG_TERM_LENGTH}
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                addEntry();
              }
            }}
          />

          <div className={classes.actions}>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_PHOTO_TYPES.join(', ')}
              hidden
              onChange={pickFromDisk}
            />
            <button
              type="button"
              className={classes.secondaryButton}
              onClick={() => fileInputRef.current?.click()}
            >
              {t('words-list:catalog.entries.uploadFromDisk')}
              <TbUpload className={classes.buttonIcon} />
            </button>

            <button
              type="button"
              className={classes.primaryButton}
              disabled={!image || !isCatalogTerm(term) || isAdding}
              onClick={addEntry}
            >
              {isAdding && <LoadingOutlined />}
              {t('words-list:catalog.entries.add')}
              <TbSquareRoundedPlus className={classes.buttonIcon} />
            </button>
          </div>
        </div>
      </div>

      {/* Lupa po lewej tylko oznacza pole — szuka się w trakcie pisania. Krzyżyk po prawej, gdy jest co czyścić */}
      <div className={classes.search}>
        <SearchOutlined className={classes.searchIcon} />
        <input
          className={classes.searchField}
          placeholder={t('words-list:catalog.entries.search')}
          aria-label={t('words-list:catalog.entries.search')}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        {filter && (
          <button
            type="button"
            className={classes.clearButton}
            aria-label={t('words-list:catalog.entries.clearSearch')}
            onClick={() => setFilter('')}
          >
            <CloseCircleFilled />
          </button>
        )}
      </div>

      {/* Licznik pozycji w prawym dolnym rogu listy. Przy szukaniu: pasujące / wszystkie.
          Pusty katalog ma już swój komunikat na liście, więc wtedy licznika nie ma */}
      <div className={classes.listBox}>
        {totalCount > 0 && (
          <Tooltip
            title={t(searchWords.length
              ? 'words-list:catalog.entries.countFilteredTooltip'
              : 'words-list:catalog.entries.countTooltip')}
          >
            <span className={classes.count}>
              <TbStack2 className={classes.countIcon} />
              {searchWords.length ? <b>{groups.length} / {totalCount}</b> : <b>{totalCount}</b>}
            </span>
          </Tooltip>
        )}

        <Spin spinning={isLoading}>
          <List
            className={classes.list}
            dataSource={groups}
            locale={{
              // Pusty katalog: ikona zamiast zdania; zdanie zostaje dla czytników ekranu
              emptyText: searchWords.length
                ? t('words-list:catalog.entries.noMatch')
                : <TbPhoto className={classes.emptyIcon} role="img" aria-label={t('words-list:catalog.entries.empty')} />,
            }}
            renderItem={(group: ConceptGroup) => (
              <List.Item
                key={group.conceptKey}
                className={classNames(classes.item, { [classes.highlighted]: group.conceptKey === highlightedKey })}
              >
                {renderTerm(group)}
                <div className={classes.images}>
                  {group.entries.map((entry) => (
                    <div key={entry.id} className={classes.imageCell}>
                      {entry.url ? (
                        <img className={classes.thumb} src={entry.url} alt={entry.conceptKey} />
                      ) : (
                        <div className={classes.noImage}>{t('words-list:catalog.entries.noImage')}</div>
                      )}
                      <Popconfirm
                        title={t('words-list:catalog.entries.withdrawConfirm')}
                        icon={null}
                        okText={t('words-list:catalog.entries.withdraw')}
                        cancelText={t('words-list:catalog.entries.cancel')}
                        okButtonProps={{ danger: true }}
                        overlayClassName={classes.confirm}
                        onConfirm={() => withdraw(entry)}
                      >
                        <button
                          type="button"
                          className={classes.withdrawButton}
                          disabled={withdrawingId === entry.id}
                          aria-label={t('words-list:catalog.entries.withdraw')}
                          title={t('words-list:catalog.entries.withdraw')}
                        >
                          {withdrawingId === entry.id
                            ? <LoadingOutlined aria-hidden="true" />
                            : <CloseOutlined aria-hidden="true" />}
                        </button>
                      </Popconfirm>
                    </div>
                  ))}
                </div>
              </List.Item>
            )}
          />
        </Spin>
      </div>
    </div>
  );
};

export default withTagManager(CatalogEntriesView);
